// =============================================================================
// Mauritius business-calendar helpers (frontend)
// =============================================================================
// Mauritius is UTC+4 with no DST. "Today" for scheduling must be the Mauritius-
// local calendar day — not the browser's/UTC day — otherwise the delivery picker
// can let a same-day booking slip through around midnight. Mirrors
// apps/backend/src/lib/dates.ts so the client greys out exactly what the server
// rejects (the backend stores delivery dates as UTC-midnight calendar days).

const TZ = 'Indian/Mauritius';

/** Mauritius-local calendar day as ISO YYYY-MM-DD (e.g. "2026-06-07"). */
export function businessTodayIso(): string {
  // en-CA renders as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Add `days` calendar days to an ISO YYYY-MM-DD day (computed in UTC). */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Human date for display, e.g. "Tue, 28 Jul" or "28 Jul 2026", in Mauritius
 * time. Stored dates (delivery date, etc.) are UTC-midnight calendar days —
 * formatting with `new Date(iso).toLocaleDateString()` and no `timeZone`
 * renders in the *browser's* local timezone instead, which silently shifts
 * the calendar day shown for any client outside Mauritius. Always use this
 * helper (or slice the ISO string) instead of calling toLocaleDateString
 * directly on a stored date.
 */
export function formatBusinessDate(
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MU', { ...opts, timeZone: TZ });
}

/** Day-of-month, short weekday and short month of a stored date, in Mauritius time — for calendar-tile UI. */
export function businessDateParts(iso: string): { day: string; weekday: string; month: string } {
  const d = new Date(iso);
  return {
    day: new Intl.DateTimeFormat('en-MU', { timeZone: TZ, day: 'numeric' }).format(d),
    weekday: new Intl.DateTimeFormat('en-MU', { timeZone: TZ, weekday: 'short' }).format(d),
    month: new Intl.DateTimeFormat('en-MU', { timeZone: TZ, month: 'short' }).format(d),
  };
}
